// src/pages/MyRsvp.tsx
// Employee RSVP page — auto-fills from GET /api/me/rsvp, submits via PUT.
// Redirects to /rsvp/confirmed?ok=1 on a successful "attending" save.
//
// Schema migration — May 2026:
//   When loading an existing RSVP, prefer the new `attendees: Attendee[]`
//   field; fall back to legacy parallel arrays (guestNames + tshirtSizes +
//   golfPlaying) for unmigrated docs. On save, ship `attendees` as the
//   source of truth — the backend re-derives the legacy fields.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import PhotoUpload from "../components/upload/PhotoUpload";

type TshirtSize = "XS" | "S" | "M" | "L" | "XL" | "XXL" | "XXXL";
type YouthSize  = "Y-XS" | "Y-S" | "Y-M" | "Y-L" | "Y-XL";
const ADULT_SIZES: TshirtSize[] = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const YOUTH_SIZES: YouthSize[]  = ["Y-XS", "Y-S", "Y-M", "Y-L", "Y-XL"];

interface AttendeeRow {
  name: string;
  type: "adult" | "child";
  tshirtSize?: TshirtSize | YouthSize;
  golfing?: boolean;
}

interface RsvpData {
  attending: boolean;
  partySize: number;
  adultsCount?: number;
  childrenCount?: number;
  guestNames?: string[];
  attendees?: AttendeeRow[];
  // Legacy shapes — both observed in production. tshirtSizes can be either
  // a flat array (older MyRsvp writes) or an object {adult,youth} (canonical).
  tshirtSizes?: string[] | { adult?: string[]; youth?: string[] };
  golfPlaying?: boolean;
  updatedAt?: string;
  verifiedAt?: string;
  source?: string;
  displayName?: string;
  raffleTicket?: number;
}

interface EventFlags {
  tshirtsEnabled?: boolean;
  golfEnabled?: boolean;
  rafflePrefix?: string;
}

/** Format the employee's raffle ticket. With a book prefix it reads as the full
 *  #####-### number (prefix padded to 5 digits, suffix to 3 — issue #35); without
 *  a prefix it's the plain number as before. */
function fmtRaffleTicket(n: number, prefix?: string): string {
  if (!prefix) return String(n);
  return `${prefix.padStart(5, "0")}-${String(n).padStart(3, "0")}`;
}

type SaveState = "idle" | "saving" | "saved" | "error";

// ── Collapsible sections (issue #47) ─────────────────────────────────────────
// The page is organized into three major sections — RSVP, Raffle, Photos —
// each minimizable, with the open/closed state persisted per user.

type SectionKey = "rsvp" | "raffle" | "photos";

// true = collapsed. RSVP starts minimized — by event week most folks have
// already RSVP'd; raffle and photos are the day-of features.
const SECTION_DEFAULTS: Record<SectionKey, boolean> = {
  rsvp: true,
  raffle: false,
  photos: false,
};

function sectionsStorageKey(user: string): string {
  return `wicnic:myrsvp:sections:${user || "anon"}`;
}

function loadSections(user: string): Record<SectionKey, boolean> {
  try {
    const raw = localStorage.getItem(sectionsStorageKey(user));
    if (raw) return { ...SECTION_DEFAULTS, ...JSON.parse(raw) };
  } catch { /* corrupted or blocked storage — fall back to defaults */ }
  return SECTION_DEFAULTS;
}

interface SectionProps {
  title: string;
  hint?: string;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

/** Collapsible page section. The whole header is the toggle; open/closed
 *  state is owned by the page so it can persist per user. */
function Section({ title, hint, collapsed, onToggle, children }: SectionProps) {
  return (
    <section className="mr-section">
      <button type="button" className="mr-section__head" onClick={onToggle} aria-expanded={!collapsed}>
        <span className="mr-section__title">{title}</span>
        {hint && <span className="mr-section__hint">{hint}</span>}
        <span className="mr-section__chev" aria-hidden="true">{collapsed ? "+" : "−"}</span>
      </button>
      {!collapsed && <div className="mr-section__body">{children}</div>}
    </section>
  );
}

/** Build a fresh attendees array for the given counts, preserving prior rows
 *  by index. Defaults: adult t-shirts to 'M', child t-shirts to 'Y-M'. */
function rebuildAttendees(
  adults: number,
  children: number,
  prior: AttendeeRow[],
): AttendeeRow[] {
  const out: AttendeeRow[] = [];
  const priorAdults = prior.filter(r => r.type === "adult");
  const priorChildren = prior.filter(r => r.type === "child");
  for (let i = 0; i < adults; i++) {
    const prev = priorAdults[i];
    out.push({
      name: prev?.name ?? "",
      type: "adult",
      tshirtSize: (prev?.tshirtSize as TshirtSize | undefined) ?? "M",
      golfing: prev?.golfing ?? false,
    });
  }
  for (let i = 0; i < children; i++) {
    const prev = priorChildren[i];
    out.push({
      name: prev?.name ?? "",
      type: "child",
      tshirtSize: (prev?.tshirtSize as YouthSize | undefined) ?? "Y-M",
    });
  }
  return out;
}

/** Hydrate an attendees array from a doc that may or may not have one.
 *  Falls back to legacy fields (guestNames + tshirtSizes + golfPlaying)
 *  for unmigrated docs. */
function hydrateAttendees(
  data: RsvpData,
  submitterName: string,
): AttendeeRow[] {
  const adults  = data.adultsCount  ?? Math.max(1, (data.partySize ?? 1) - (data.childrenCount ?? 0));
  const children = data.childrenCount ?? 0;

  // Prefer explicit attendees array
  if (Array.isArray(data.attendees) && data.attendees.length > 0) {
    return data.attendees.map((a, i) => ({
      name: i === 0 ? submitterName || a.name || "" : a.name || "",
      type: a.type === "child" ? "child" : "adult",
      tshirtSize: a.tshirtSize,
      golfing: a.type === "adult" ? (a.golfing === true) : undefined,
    }));
  }

  // Legacy hydration. Names: position 0 = submitter, then guestNames[].
  const names: string[] = [submitterName, ...(data.guestNames ?? [])];

  // T-shirts can be flat array (legacy MyRsvp) or {adult,youth} (canonical).
  let adultSizes: string[] = [];
  let youthSizes: string[] = [];
  if (Array.isArray(data.tshirtSizes)) {
    adultSizes = data.tshirtSizes;  // flat: assume all adult sizes
  } else if (data.tshirtSizes && typeof data.tshirtSizes === "object") {
    adultSizes = data.tshirtSizes.adult ?? [];
    youthSizes = data.tshirtSizes.youth ?? [];
  }

  // Legacy `golfPlaying: true` = the submitter is golfing. We can't tell
  // from a single bool whether anyone else in the party is. Best effort.
  const submitterGolfing = data.golfPlaying === true;

  const out: AttendeeRow[] = [];
  for (let i = 0; i < adults; i++) {
    const sz = adultSizes[i];
    out.push({
      name: i === 0 ? names[0] || "" : names[i] || "",
      type: "adult",
      tshirtSize: (ADULT_SIZES as string[]).includes(sz) ? (sz as TshirtSize) : "M",
      golfing: i === 0 ? submitterGolfing : false,
    });
  }
  for (let i = 0; i < children; i++) {
    const sz = youthSizes[i];
    const namesIdx = adults + i;
    out.push({
      name: names[namesIdx] || "",
      type: "child",
      tshirtSize: (YOUTH_SIZES as string[]).includes(sz) ? (sz as YouthSize) : "Y-M",
    });
  }
  return out;
}

export default function MyRsvp() {
  const { getApiToken, account } = useAuth();
  const navigate = useNavigate();

  const [existing, setExisting] = useState<RsvpData | null>(null);
  const [attending, setAttending] = useState<boolean | null>(null);
  const [adults, setAdults]       = useState(1);
  const [children, setChildren]   = useState(0);
  const [attendees, setAttendees] = useState<AttendeeRow[]>([
    { name: "", type: "adult", tshirtSize: "M", golfing: false },
  ]);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [tshirtsEnabled, setTshirtsEnabled] = useState<boolean>(true);
  const [golfEnabled, setGolfEnabled]       = useState<boolean>(false);
  const [rafflePrefix, setRafflePrefix]     = useState<string | undefined>(undefined);
  const [hydrated, setHydrated]   = useState(false);
  const [yearOptions, setYearOptions] = useState<string[]>(["2026"]);

  const partySize = adults + children;
  const submitterName = account?.name ?? account?.username ?? "";

  // ── collapsible sections: per-user persisted open/closed state ────────────
  const userKey = (account?.username ?? "").toLowerCase();
  const [collapsed, setCollapsed] = useState<Record<SectionKey, boolean>>(SECTION_DEFAULTS);

  // Re-read once the MSAL account resolves (userKey is "" on first render).
  useEffect(() => { setCollapsed(loadSections(userKey)); }, [userKey]);

  function toggleSection(key: SectionKey) {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(sectionsStorageKey(userKey), JSON.stringify(next)); } catch { /* storage unavailable */ }
      return next;
    });
  }

  const raffleTicketNum =
    existing?.attending === true && typeof existing.raffleTicket === "number"
      ? existing.raffleTicket
      : null;

  // ── load event flags ──────────────────────────────────────────────────────
  useEffect(() => {
    fetch("/api/public/event?year=2026")
      .then(r => r.ok ? r.json() : null)
      .then((d: EventFlags | null) => {
        if (d) {
          setTshirtsEnabled(d.tshirtsEnabled !== false);
          setGolfEnabled(d.golfEnabled === true);
          setRafflePrefix(d.rafflePrefix || undefined);
        }
      })
      .catch(() => { /* defaults: tshirts on, golf off */ });
  }, []);

  // ── load gallery year list for the photo uploader (issue #43) ─────────────
  // Mirrors Gallery.tsx; falls back to the current year on any failure.
  useEffect(() => {
    fetch("/api/public/years")
      .then(r => r.json())
      .then(d => {
        const years: string[] = Array.isArray(d.years) ? d.years : [];
        const unique = Array.from(new Set(years)).sort().reverse();
        if (unique.length) setYearOptions(unique);
      })
      .catch(() => { /* keep ["2026"] fallback */ });
  }, []);

  // ── load existing RSVP ────────────────────────────────────────────────────
  // Any failure here just leaves the user in "no existing RSVP" state and they
  // submit a fresh one. We don't show an error — first-time users shouldn't see one.
  useEffect(() => {
    (async () => {
      try {
        const token = await getApiToken();
        const res = await fetch("/api/me/rsvp", { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { setHydrated(true); return; }
        const data: RsvpData | null = await res.json();
        if (!data) { setHydrated(true); return; }

        setExisting(data);
        setAttending(data.attending);

        const a = data.adultsCount ?? Math.max(1, (data.partySize ?? 1) - (data.childrenCount ?? 0));
        const c = data.childrenCount ?? 0;
        setAdults(Math.max(1, a));
        setChildren(Math.max(0, c));

        setAttendees(hydrateAttendees(data, submitterName));
      } catch {
        // swallow — first-time submission flow handles it
      } finally {
        setHydrated(true);
      }
    })();
    // submitterName is read once at hydration; intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── keep the attendees array in sync with adults/children counts ──────────
  // Only after hydration finishes — otherwise we'd overwrite the loaded data
  // with empty defaults before it can render.
  useEffect(() => {
    if (!hydrated) return;
    setAttendees(prev => rebuildAttendees(adults, children, prev));
  }, [adults, children, hydrated]);

  // First adult row's name follows the submitter's MSAL displayName so it's
  // never blank in the UI. Backend re-derives displayName from the JWT anyway.
  useEffect(() => {
    if (!hydrated || !submitterName) return;
    setAttendees(prev => {
      if (prev.length === 0 || prev[0].name === submitterName) return prev;
      const next = [...prev];
      next[0] = { ...next[0], name: submitterName };
      return next;
    });
  }, [submitterName, hydrated]);

  function updateAdults(n: number) {
    setAdults(Math.max(1, Math.min(10, n)));
  }
  function updateChildren(n: number) {
    setChildren(Math.max(0, Math.min(10, n)));
  }
  function updateRow<K extends keyof AttendeeRow>(idx: number, key: K, val: AttendeeRow[K]) {
    setAttendees(prev => prev.map((row, i) => (i === idx ? { ...row, [key]: val } : row)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (attending === null) return;
    setSaveState("saving");

    try {
      const token = await getApiToken();
      const idClaims = account?.idTokenClaims as { email?: string; preferred_username?: string } | undefined;
      const userEmail = idClaims?.email ?? idClaims?.preferred_username ?? account?.username;

      // Strip fields the backend won't accept anyway:
      //  - tshirtSize unless tshirtsEnabled (employee form: always employee)
      //  - golfing unless golfEnabled
      //  - golfing on child rows always
      const payloadAttendees = attending ? attendees.map(row => {
        const out: AttendeeRow = { name: row.name.trim(), type: row.type };
        if (tshirtsEnabled && row.tshirtSize) out.tshirtSize = row.tshirtSize;
        if (golfEnabled && row.type === "adult") out.golfing = row.golfing === true;
        return out;
      }) : [];

      const payload = {
        displayName: account?.name ?? undefined,
        email: userEmail,
        attending,
        partySize: attending ? partySize : 0,
        adultsCount: attending ? adults : 0,
        childrenCount: attending ? children : 0,
        attendees: attending ? payloadAttendees : undefined,
      };

      const res = await fetch("/api/me/rsvp", {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      setExisting(updated);

      if (attending) {
        navigate("/rsvp/confirmed?ok=1");
        return;
      }

      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 3500);
    } catch (err) {
      setSaveState("error");
    }
  }

  const displayName = account?.name ?? account?.username ?? "there";
  const isUpdate    = !!existing;

  return (
    <>
      <style>{`
        .my-rsvp-page { min-height: 100vh; background: var(--bg); padding-bottom: 5rem; }
        .rsvp-hero { background: var(--black); color: var(--white); padding: 4rem 4rem 3.5rem; }
        .rsvp-hero-inner { max-width: 680px; margin: 0 auto; }
        .eyebrow { font-family: var(--font-display); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); margin-bottom: 0.75rem; }
        .rsvp-hero h1 { font-family: var(--font-display); font-size: clamp(1.8rem, 4vw, 2.8rem); font-weight: 700; text-transform: uppercase; margin: 0 0 0.5rem; }
        .rsvp-hero p { font-family: var(--font-body); font-size: 1rem; color: rgba(255,255,255,0.7); margin: 0; }
        .rsvp-form-wrap { max-width: 680px; margin: 0 auto; padding: 3rem 4rem; }
        @media (max-width: 640px) {
          .rsvp-form-wrap { padding: 2rem 1.5rem; }
          .rsvp-hero { padding: 3rem 1.5rem 2.5rem; }
        }
        .status-badge { display: inline-flex; align-items: center; gap: 0.5rem; font-family: var(--font-display); font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; padding: 0.4rem 0.85rem; margin-bottom: 2rem; }
        .badge-submitted { background: #e8f5e9; color: #2e7d32; }
        .badge-pending   { background: #fff8e1; color: #f57f17; }
        .attend-choice { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 2.5rem; }
        .attend-btn { border: 2px solid var(--border); background: var(--white); padding: 1.5rem 1rem; text-align: center; cursor: pointer; transition: all 0.15s; }
        .attend-btn.yes.active { border-color: var(--red); background: var(--red); }
        .attend-btn.no.active  { border-color: var(--mid); background: var(--mid); }
        .attend-btn:hover:not(.active) { border-color: var(--mid); }
        .attend-btn span { display: block; font-family: var(--font-display); font-size: 1rem; font-weight: 700; text-transform: uppercase; color: var(--mid); }
        .attend-btn.active span { color: var(--white); }
        .attend-btn small { display: block; font-family: var(--font-body); font-size: 0.8rem; color: var(--muted); margin-top: 0.25rem; }
        .attend-btn.active small { color: rgba(255,255,255,0.75); }
        .field-group { margin-bottom: 2rem; }
        .field-label { display: block; font-family: var(--font-display); font-size: 0.7rem; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: var(--red); margin-bottom: 0.4rem; }
        .field-hint { display: block; font-family: var(--font-body); font-size: 0.85rem; color: var(--muted); margin-bottom: 0.85rem; }
        .party-counters-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media (max-width: 540px) { .party-counters-row { grid-template-columns: 1fr; } }
        .counter-block { background: var(--white); border: 2px solid var(--border); padding: 1.25rem; }
        .counter-block-label { font-family: var(--font-display); font-size: 0.85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--black); }
        .counter-block-sub { font-family: var(--font-body); font-size: 0.78rem; color: var(--muted); margin-bottom: 0.6rem; }
        .party-stepper { display: inline-flex; border: 2px solid var(--border); }
        .stepper-btn { width: 38px; height: 38px; border: none; background: var(--white); font-size: 1.15rem; cursor: pointer; color: var(--charcoal); transition: background 0.15s, color 0.15s; }
        .stepper-btn:hover { background: var(--red); color: var(--white); }
        .stepper-val { width: 56px; height: 38px; border: none; border-left: 2px solid var(--border); border-right: 2px solid var(--border); font-family: var(--font-display); font-size: 1rem; font-weight: 700; text-align: center; color: var(--black); outline: none; }
        .submit-row { display: flex; align-items: center; gap: 1.25rem; margin-top: 2.5rem; flex-wrap: wrap; }
        .submit-btn { font-family: var(--font-display); font-size: 0.85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--white); background: var(--red); border: none; padding: 0.85rem 2rem; cursor: pointer; transition: background 0.15s; border-radius: 0; }
        .submit-btn:hover:not(:disabled) { background: var(--red-dark); }
        .submit-btn:disabled { background: var(--muted); cursor: not-allowed; }
        .save-feedback { font-family: var(--font-body); font-size: 0.9rem; color: var(--muted); }
        .save-feedback.success { color: #2e7d32; }
        .save-feedback.error   { color: var(--red); }
        .section-divider { border: none; border-top: 1px solid var(--border); margin: 2rem 0; }

        /* Collapsible sections (issue #47) */
        .mr-section { border: 2px solid var(--border); background: var(--white); margin-bottom: 1.25rem; }
        .mr-section__head { display: flex; align-items: center; gap: 0.85rem; width: 100%; border: none; background: var(--white); padding: 1rem 1.25rem; cursor: pointer; text-align: left; }
        .mr-section__title { font-family: var(--font-display); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--black); }
        .mr-section__hint { font-family: var(--font-body); font-size: 0.82rem; color: var(--muted); margin-left: auto; text-align: right; }
        .mr-section__chev { font-family: var(--font-display); font-size: 1.25rem; font-weight: 700; color: var(--mid); line-height: 1; flex: 0 0 auto; }
        .mr-section__head:hover .mr-section__chev { color: var(--red); }
        .mr-section__body { padding: 1.5rem 1.25rem; border-top: 2px solid var(--border); }
        @media (max-width: 640px) { .mr-section__body { padding: 1.25rem 1rem; } }

        /* Per-attendee rows */
        .attendees-grid { display: flex; flex-direction: column; gap: 0.6rem; }
        .attendee-row {
          display: grid;
          grid-template-columns: minmax(80px, 100px) 1fr auto auto;
          gap: 0.75rem;
          align-items: center;
          padding: 0.6rem 0.85rem;
          border: 2px solid var(--border);
          background: var(--white);
        }
        @media (max-width: 640px) {
          .attendee-row { grid-template-columns: 1fr; gap: 0.5rem; }
        }
        .attendee-row__who {
          font-family: var(--font-display); font-weight: 600; font-size: 0.85rem;
          text-transform: uppercase; letter-spacing: 0.05em; color: var(--charcoal);
        }
        .attendee-row__name {
          border: 1px solid var(--border);
          padding: 0.45rem 0.7rem;
          font-family: var(--font-body); font-size: 0.95rem;
          background: var(--white); min-width: 0;
        }
        .attendee-row__name:disabled {
          background: var(--bg); color: var(--steel); cursor: not-allowed;
        }
        .attendee-row__select {
          font-family: var(--font-body); font-size: 0.95rem;
          padding: 0.4rem 0.55rem; border: 1px solid var(--border);
          background: var(--white); cursor: pointer;
        }
        .attendee-row__golf {
          display: inline-flex; align-items: center; gap: 0.4rem;
          font-family: var(--font-body); font-size: 0.9rem;
          color: var(--steel); cursor: pointer; white-space: nowrap;
        }
        .attendee-row__golf input[type="checkbox"] {
          accent-color: var(--red); width: 1.05rem; height: 1.05rem; cursor: pointer;
        }

        /* Raffle ticket (issue #16) — shown once an admin assigns numbers */
        .raffle-ticket {
          display: flex; align-items: stretch; margin: 0;
          background: var(--red); color: var(--white);
          box-shadow: 0 6px 20px rgba(192,57,43,0.25);
        }
        .raffle-ticket__body { flex: 1; padding: 1.25rem 1.5rem; }
        .raffle-ticket__eyebrow {
          font-family: var(--font-display); font-size: 0.68rem; font-weight: 600;
          letter-spacing: 0.3em; text-transform: uppercase; opacity: 0.85; margin: 0 0 0.35rem;
        }
        .raffle-ticket__num {
          font-family: var(--font-display); font-weight: 700; line-height: 1;
          font-size: clamp(2.4rem, 7vw, 3.4rem); letter-spacing: 0.04em;
          font-variant-numeric: tabular-nums; margin: 0;
        }
        .raffle-ticket__hint {
          font-family: var(--font-body); font-size: 0.82rem; opacity: 0.85; margin: 0.5rem 0 0;
        }
        /* Perforated stub: dashed divider + punch-out notches top & bottom */
        .raffle-ticket__stub {
          position: relative; width: 58px; flex: 0 0 58px;
          border-left: 2px dashed rgba(255,255,255,0.55);
          display: flex; align-items: center; justify-content: center;
        }
        .raffle-ticket__stub::before,
        .raffle-ticket__stub::after {
          content: ""; position: absolute; left: -9px; width: 16px; height: 16px;
          background: var(--white); border-radius: 50%;
        }
        .raffle-ticket__stub::before { top: -8px; }
        .raffle-ticket__stub::after { bottom: -8px; }
        .raffle-ticket__admit {
          font-family: var(--font-display); font-size: 0.7rem; font-weight: 700;
          letter-spacing: 0.25em; text-transform: uppercase;
          writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap;
        }
        .raffle-results-link { color: var(--red); font-weight: 600; text-decoration: none; }
        .raffle-results-link:hover { text-decoration: underline; }
      `}</style>

      <div className="my-rsvp-page">
        <div className="rsvp-hero">
          <div className="rsvp-hero-inner">
            <p className="eyebrow">— Employee RSVP</p>
            <h1>Your WICnic RSVP</h1>
            <p>Hey {displayName} — let us know if you're coming to WICnic 2026.</p>
          </div>
        </div>

        <div className="rsvp-form-wrap">
          <Section
            title="RSVP"
            hint={existing
              ? existing.attending ? "You're on the list" : "Not attending"
              : "Not submitted yet"}
            collapsed={collapsed.rsvp}
            onToggle={() => toggleSection("rsvp")}
          >
          {existing && (
            <div className={`status-badge ${existing.verifiedAt ? "badge-submitted" : "badge-pending"}`}>
              {existing.attending
                ? existing.verifiedAt ? "✓ You're on the list" : "⏳ RSVP saved"
                : "✓ RSVP saved — not attending"}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="field-group">
              <span className="field-label">Will you be attending?</span>
              <div className="attend-choice">
                <button type="button" className={`attend-btn yes${attending === true ? " active" : ""}`} onClick={() => setAttending(true)}>
                  <span>✓ Yes, I'm in</span>
                  <small>Coming to WICnic 2026</small>
                </button>
                <button type="button" className={`attend-btn no${attending === false ? " active" : ""}`} onClick={() => setAttending(false)}>
                  <span>✗ Can't make it</span>
                  <small>Won't be attending</small>
                </button>
              </div>
            </div>

            {attending === true && (
              <>
                <hr className="section-divider" />

                <div className="field-group">
                  <span className="field-label">Your party</span>
                  <span className="field-hint">How many adults and children should we plan for?</span>
                  <div className="party-counters-row">
                    <div className="counter-block">
                      <div className="counter-block-label">Adults</div>
                      <div className="counter-block-sub">Including yourself</div>
                      <div className="party-stepper">
                        <button type="button" className="stepper-btn" onClick={() => updateAdults(adults - 1)} aria-label="Decrease adults">−</button>
                        <input className="stepper-val" type="number" min={1} max={10} value={adults}
                          onChange={(e) => updateAdults(parseInt(e.target.value, 10) || 1)} />
                        <button type="button" className="stepper-btn" onClick={() => updateAdults(adults + 1)} aria-label="Increase adults">+</button>
                      </div>
                    </div>
                    <div className="counter-block">
                      <div className="counter-block-label">Children</div>
                      <div className="counter-block-sub">Under 18</div>
                      <div className="party-stepper">
                        <button type="button" className="stepper-btn" onClick={() => updateChildren(children - 1)} aria-label="Decrease children">−</button>
                        <input className="stepper-val" type="number" min={0} max={10} value={children}
                          onChange={(e) => updateChildren(parseInt(e.target.value, 10) || 0)} />
                        <button type="button" className="stepper-btn" onClick={() => updateChildren(children + 1)} aria-label="Increase children">+</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="field-group">
                  <span className="field-label">Attendees</span>
                  <span className="field-hint">Names help with nametags and check-in. Kids' names are optional.</span>
                  <div className="attendees-grid">
                    {attendees.map((row, i) => {
                      const isAdult = row.type === "adult";
                      const adultIdx = attendees.slice(0, i + 1).filter(r => r.type === "adult").length;
                      const childIdx = attendees.slice(0, i + 1).filter(r => r.type === "child").length;
                      const label =
                        i === 0 ? "You" :
                        isAdult ? `Adult ${adultIdx}` :
                                  `Child ${childIdx}`;

                      return (
                        <div key={i} className="attendee-row">
                          <span className="attendee-row__who">{label}</span>
                          <input
                            className="attendee-row__name"
                            type="text"
                            value={row.name}
                            onChange={(e) => updateRow(i, "name", e.target.value)}
                            maxLength={120}
                            placeholder={isAdult ? "Name" : "Child's name (optional)"}
                            disabled={i === 0}
                            aria-label={`${label} name`}
                          />
                          {tshirtsEnabled && (
                            <select
                              className="attendee-row__select"
                              value={row.tshirtSize ?? (isAdult ? "M" : "Y-M")}
                              onChange={(e) => updateRow(i, "tshirtSize", e.target.value as TshirtSize | YouthSize)}
                              aria-label={`${label} t-shirt size`}
                            >
                              {(isAdult ? ADULT_SIZES : YOUTH_SIZES).map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          )}
                          {golfEnabled && isAdult && (
                            <label className="attendee-row__golf">
                              <input
                                type="checkbox"
                                checked={row.golfing === true}
                                onChange={(e) => updateRow(i, "golfing", e.target.checked)}
                              />
                              <span>⛳ Golfing</span>
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {golfEnabled && (
                    <p className="field-hint" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
                      Golf — Riverdale Dunes, 8:30 AM shotgun the morning of WICnic. WIC covers green fees and carts.
                    </p>
                  )}
                </div>
              </>
            )}

            {attending !== null && (
              <div className="submit-row">
                <button type="submit" className="submit-btn" disabled={saveState === "saving"}>
                  {saveState === "saving" ? "Saving…" : isUpdate ? "Update RSVP" : "Submit RSVP"}
                </button>
                {saveState === "saved" && <span className="save-feedback success">✓ Saved!</span>}
                {saveState === "error" && <span className="save-feedback error">Something went wrong. Please try again.</span>}
              </div>
            )}
          </form>
          </Section>

          <Section
            title="Raffle"
            hint={raffleTicketNum !== null
              ? `Ticket No. ${fmtRaffleTicket(raffleTicketNum, rafflePrefix)}`
              : "Drawing at the picnic"}
            collapsed={collapsed.raffle}
            onToggle={() => toggleSection("raffle")}
          >
            {raffleTicketNum !== null ? (
              <div className="raffle-ticket" role="img"
                aria-label={`Your WICnic raffle ticket number is ${fmtRaffleTicket(raffleTicketNum, rafflePrefix)}`}>
                <div className="raffle-ticket__body">
                  <p className="raffle-ticket__eyebrow">WICnic Raffle</p>
                  <p className="raffle-ticket__num">No. {fmtRaffleTicket(raffleTicketNum, rafflePrefix)}</p>
                  <p className="raffle-ticket__hint">Your ticket for the WICnic raffle drawing — no need to print anything.</p>
                </div>
                <div className="raffle-ticket__stub">
                  <span className="raffle-ticket__admit">Admit One</span>
                </div>
              </div>
            ) : (
              <p className="field-hint" style={{ margin: 0 }}>
                No ticket yet — raffle numbers are assigned to attending employees
                before the drawing. RSVP "Yes" and check back.
              </p>
            )}
            <p className="field-hint" style={{ margin: "1rem 0 0" }}>
              <Link to="/raffle" className="raffle-results-link">See raffle prizes &amp; results →</Link>
            </p>
          </Section>

          <Section
            title="Photos"
            hint="Share your WICnic pics"
            collapsed={collapsed.photos}
            onToggle={() => toggleSection("photos")}
          >
            <span className="field-hint">
              Have photos from a WICnic? Share them with everyone — uploads are
              reviewed before they appear in the gallery.
            </span>
            <PhotoUpload defaultYear="2026" availableYears={yearOptions} />
          </Section>
        </div>
      </div>
    </>
  );
}
